import { useRef } from "react";
import { Switch } from "../components/ui/switch";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import LanguageSelector from "../components/LanguageSelector";
import SearchBox from "../components/SearchBox";
import { BASE_LAYERS } from "../components/MapView";
import { useLang } from "../contexts/LanguageContext";

export default function Toolbar({
    trackName,
    onRenameTrack,
    mode,
    onModeChange,
    onUndo,
    canUndo,
    onClear,
    onImportFile,
    onExport,
    onFetchElevation,
    elevationLoading,
    onAnalyzeSurface,
    surfaceLoading,
    onSaveLocal,
    savedTracks,
    onLoadSaved,
    onDeleteSaved,
    onToggleOverlay,
    isOverlayed,
    onNewTrack,
    onSnap,
    snapLoading,
    offroad,
    onToggleOffroad,
    offroadGrade,
    onChangeGrade,
    onShare,
    minimized,
    onToggleMinimize,
    onSearchSelect,
    onZoomIn,
    onZoomOut,
    activeLayer,
    onChangeLayer,
    showPanel,
    onTogglePanel,
}) {
    const { t } = useLang();
    const fileInputRef = useRef(null);

    return (
        <div className="rf-card flex flex-wrap items-center gap-2 px-3 py-2 fade-up">
            <div className="flex items-center gap-2 pr-3 sm:border-r border-[var(--border-soft)]">
                <button
                    onClick={onToggleMinimize}
                    className="rf-btn !px-2"
                    data-testid="toolbar-toggle"
                    title={minimized ? t("expandTools") : t("minimizeTools")}
                >
                    <i
                        className={`fa-solid ${
                            minimized ? "fa-bars" : "fa-chevron-up"
                        }`}
                    ></i>
                </button>
                <img
                    src="https://customer-assets.emergentagent.com/job_route-mapper-110/artifacts/cpx42va3_cropped-VulkanoProjectsLogoBLTraspa-300x236-1.png"
                    alt="Vulkano Projects"
                    className="h-9 sm:h-10 w-auto"
                    data-testid="brand-logo"
                />
                <input
                    type="text"
                    value={trackName}
                    onChange={(e) => onRenameTrack(e.target.value)}
                    className="bg-transparent outline-none font-display font-semibold text-[15px] text-[var(--text-primary)] focus:text-[var(--accent)] w-[140px] sm:w-[260px]"
                    placeholder={t("trackNamePlaceholder")}
                    data-testid="track-name-input"
                />
                <LanguageSelector />
            </div>

            {!minimized && (
              <>
              <div
                className="flex items-center gap-1 rounded-full p-1"
                style={{
                    background: "var(--bg-elev)",
                    border: "1px solid var(--border-strong)",
                }}
            >
                <button
                    className={`mode-pill ${mode === "track" ? "active" : ""}`}
                    onClick={() => onModeChange("track")}
                    data-testid="mode-track"
                    title={t("modeTrackTitle")}
                >
                    <i className="fa-solid fa-location-crosshairs"></i> {t("modeTrack")}
                </button>
                <button
                    className={`mode-pill ${mode === "poi" ? "active" : ""}`}
                    onClick={() => onModeChange("poi")}
                    data-testid="mode-poi"
                    title={t("modePoiTitle")}
                >
                    <i className="fa-solid fa-bookmark"></i> {t("modePoi")}
                </button>
                <button
                    className={`mode-pill ${mode === "view" ? "active" : ""}`}
                    onClick={() => onModeChange("view")}
                    data-testid="mode-view"
                    title={t("modeViewTitle")}
                >
                    <i className="fa-solid fa-hand"></i> {t("modeView")}
                </button>
            </div>

            <SearchBox onSelect={onSearchSelect} />

            <div
                className="flex items-center gap-1 rounded-full p-1"
                style={{
                    background: "var(--bg-elev)",
                    border: "1px solid var(--border-strong)",
                }}
            >
                <button
                    className="rf-btn !px-2"
                    onClick={onZoomOut}
                    data-testid="zoom-out-btn"
                    title={t("zoomOut")}
                >
                    <i className="fa-solid fa-minus"></i>
                </button>
                <button
                    className="rf-btn !px-2"
                    onClick={onZoomIn}
                    data-testid="zoom-in-btn"
                    title={t("zoomIn")}
                >
                    <i className="fa-solid fa-plus"></i>
                </button>
            </div>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="rf-btn"
                        data-testid="layers-btn"
                        title={t("layers")}
                    >
                        <i className="fa-solid fa-map"></i> {t("layers")}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="bg-[var(--bg-card-solid)] border border-[var(--border-strong)] text-[var(--text-primary)] !z-[2000] min-w-[200px]"
                    align="start"
                >
                    <DropdownMenuLabel className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider">
                        {t("layers")}
                    </DropdownMenuLabel>
                    {BASE_LAYERS.map((l) => (
                        <DropdownMenuItem
                            key={l.key}
                            className={`cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)] ${
                                activeLayer === l.key
                                    ? "text-[var(--accent)]"
                                    : ""
                            }`}
                            onClick={() => onChangeLayer(l.key)}
                            data-testid={`layer-${l.key}`}
                        >
                            <i
                                className={`fa-solid ${
                                    activeLayer === l.key
                                        ? "fa-circle-dot"
                                        : "fa-circle"
                                } mr-2 text-[11px]`}
                            ></i>
                            {l.i18n ? t(l.i18n) : l.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <button
                className={`rf-btn ${showPanel ? "primary" : ""}`}
                onClick={onTogglePanel}
                data-testid="toggle-points-btn"
                title={t("trackPointsToggle")}
            >
                <i className="fa-solid fa-list-ol"></i> {t("trackPointsToggle")}
            </button>

            <button
                className="rf-btn"
                onClick={onUndo}
                disabled={!canUndo}
                data-testid="undo-btn"
                title={t("undoTitle")}
            >
                <i className="fa-solid fa-rotate-left"></i> {t("undo")}
            </button>

            <button
                className="rf-btn"
                onClick={onFetchElevation}
                disabled={elevationLoading}
                data-testid="elevation-btn"
                title={t("elevationTitle")}
            >
                <i
                    className={`fa-solid ${
                        elevationLoading ? "fa-spinner fa-spin" : "fa-mountain"
                    }`}
                ></i>
                {t("elevation")}
            </button>

            <button
                className="rf-btn"
                onClick={onAnalyzeSurface}
                disabled={surfaceLoading}
                data-testid="analyze-surface-btn"
                title={t("surfaceTitle")}
            >
                <i
                    className={`fa-solid ${
                        surfaceLoading ? "fa-spinner fa-spin" : "fa-layer-group"
                    }`}
                ></i>
                {t("surface")}
            </button>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={`rf-btn ${offroad ? "primary" : ""}`}
                        disabled={snapLoading}
                        data-testid="snap-btn"
                        title={t("autoTraceTitle")}
                    >
                        <i
                            className={`fa-solid ${
                                snapLoading
                                    ? "fa-spinner fa-spin"
                                    : offroad
                                      ? "fa-mountain-sun"
                                      : "fa-road"
                            }`}
                        ></i>
                        {t("autoTrace")}
                        {offroad && <span className="chip ml-1">OFFROAD</span>}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="bg-[var(--bg-card-solid)] border border-[var(--border-strong)] text-[var(--text-primary)] !z-[2000] min-w-[260px]"
                    align="start"
                >
                    <div
                        className="flex items-center justify-between gap-3 px-2 py-2 rounded-md"
                        style={{ background: "var(--bg-elev)" }}
                        data-testid="offroad-row"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col">
                            <span className="text-[12px] font-semibold text-[var(--text-primary)]">
                                <i className="fa-solid fa-mountain-sun mr-1.5 text-[var(--accent)]"></i>
                                {t("offroad")}
                            </span>
                            <span className="text-[10px] text-[var(--text-faint)] leading-tight max-w-[180px]">
                                {t("offroadDesc")}
                            </span>
                        </div>
                        <Switch
                            checked={offroad}
                            onCheckedChange={onToggleOffroad}
                            data-testid="offroad-switch"
                        />
                    </div>
                    <DropdownMenuSeparator className="bg-[var(--border-strong)]" />
                    {offroad && (
                        <div
                            className="px-2 py-2"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="grade-row"
                        >
                            <div className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider mb-1.5">
                                {t("trackDifficulty")}
                            </div>
                            <div className="flex items-center gap-1">
                                {[
                                    { g: 1, label: t("grade1"), desc: t("grade1desc") },
                                    { g: 2, label: t("grade2"), desc: t("grade2desc") },
                                    { g: 3, label: t("grade3"), desc: t("grade3desc") },
                                ].map((opt) => (
                                    <button
                                        key={opt.g}
                                        onClick={() => onChangeGrade(opt.g)}
                                        className={`grade-pill ${
                                            offroadGrade === opt.g ? "active" : ""
                                        }`}
                                        title={opt.desc}
                                        data-testid={`grade-${opt.g}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="text-[10px] text-[var(--text-faint)] mt-1">
                                {offroadGrade === 1
                                    ? t("grade1hint")
                                    : offroadGrade === 2
                                      ? t("grade2hint")
                                      : t("grade3hint")}
                            </div>
                        </div>
                    )}
                    <DropdownMenuLabel className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider">
                        {offroad ? t("brouterLabel") : t("osrmLabel")}
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onSnap("driving")}
                        data-testid="snap-driving"
                    >
                        <i className="fa-solid fa-car mr-2"></i> {t("carMoto")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onSnap("cycling")}
                        data-testid="snap-cycling"
                    >
                        <i className="fa-solid fa-bicycle mr-2"></i> {t("bike")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onSnap("foot")}
                        data-testid="snap-foot"
                    >
                        <i className="fa-solid fa-person-walking mr-2"></i> {t("onFoot")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <button
                className="rf-btn"
                onClick={onShare}
                data-testid="share-btn"
                title={t("shareBtnTitle")}
            >
                <i className="fa-solid fa-share-nodes"></i> {t("share")}
            </button>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="rf-btn" data-testid="import-export-btn">
                        <i className="fa-solid fa-arrows-up-down"></i> {t("importExport")}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="bg-[var(--bg-card-solid)] border border-[var(--border-strong)] text-[var(--text-primary)] !z-[2000]"
                    align="start"
                >
                    <DropdownMenuLabel className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider">
                        {t("importLabel")}
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="import-file"
                    >
                        <i className="fa-solid fa-file-arrow-up mr-2"></i>
                        {t("importFile")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[var(--border-strong)]" />
                    <DropdownMenuLabel className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider">
                        {t("exportLabel")}
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onExport("gpx")}
                        data-testid="export-gpx"
                    >
                        <i className="fa-solid fa-file-export mr-2"></i> GPX
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onExport("kml")}
                        data-testid="export-kml"
                    >
                        <i className="fa-solid fa-file-export mr-2"></i> KML
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="cursor-pointer focus:bg-[var(--accent-soft)] focus:text-[var(--accent)]"
                        onClick={() => onExport("geojson")}
                        data-testid="export-geojson"
                    >
                        <i className="fa-solid fa-file-export mr-2"></i> GeoJSON
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="rf-btn" data-testid="library-btn">
                        <i className="fa-solid fa-folder-open"></i> {t("library")}
                        <span className="chip ml-1">{savedTracks.length}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="bg-[var(--bg-card-solid)] border border-[var(--border-strong)] text-[var(--text-primary)] max-h-[300px] overflow-y-auto min-w-[260px] !z-[2000]"
                    align="start"
                >
                    <DropdownMenuLabel className="text-[var(--text-faint)] text-[10px] uppercase tracking-wider">
                        {t("savedRoutes")}
                    </DropdownMenuLabel>
                    {savedTracks.length === 0 && (
                        <div className="px-2 py-3 text-xs text-[var(--text-faint)]">
                            {t("noSavedRoutes")}
                        </div>
                    )}
                    {savedTracks.length > 0 && (
                        <div
                            className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-[var(--border-soft)] cursor-pointer hover:bg-[var(--accent-soft)] rounded"
                            onClick={onToggleOverlay}
                            data-testid="toggle-overlay"
                        >
                            <span className="text-[11px] text-[var(--text-muted)]">
                                <i className="fa-solid fa-layer-group mr-1"></i>{" "}
                                {t("showOtherRoutes")}
                            </span>
                            <span
                                className={`chip ${
                                    isOverlayed
                                        ? "text-[var(--accent)] border-[var(--accent)]"
                                        : ""
                                }`}
                            >
                                {isOverlayed ? "ON" : "OFF"}
                            </span>
                        </div>
                    )}
                    {savedTracks.map((tr) => (
                        <div
                            key={tr.id}
                            className="flex items-center justify-between px-2 py-1.5 hover:bg-[var(--accent-soft)] rounded text-xs cursor-pointer group"
                            onClick={() => onLoadSaved(tr.id)}
                            data-testid={`load-track-${tr.id}`}
                        >
                            <div className="flex flex-col">
                                <span className="font-medium text-[var(--text-primary)] group-hover:text-[var(--accent)]">
                                    {tr.name}
                                </span>
                                <span className="text-[10px] text-[var(--text-faint)] font-mono-stat">
                                    {tr.points?.length || 0} {t("pts")} ·{" "}
                                    {new Date(tr.savedAt).toLocaleDateString()}
                                </span>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteSaved(tr.id);
                                }}
                                className="text-[var(--text-faint)] hover:text-[var(--danger)] px-2"
                                data-testid={`delete-track-${tr.id}`}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <button
                className="rf-btn primary"
                onClick={onSaveLocal}
                data-testid="save-btn"
            >
                <i className="fa-solid fa-bookmark"></i> {t("save")}
            </button>
            <button
                className="rf-btn"
                onClick={onNewTrack}
                data-testid="new-track-btn"
                title={t("newRouteTitle")}
            >
                <i className="fa-solid fa-plus"></i> {t("newRoute")}
            </button>
            <button
                className="rf-btn danger"
                onClick={onClear}
                data-testid="clear-btn"
                title={t("clearTitle")}
            >
                <i className="fa-solid fa-trash"></i>
            </button>
              </>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept=".gpx,.kml,.geojson,.json,application/gpx+xml,application/vnd.google-earth.kml+xml,application/json"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onImportFile(f);
                    e.target.value = "";
                }}
                data-testid="file-input"
            />
        </div>
    );
}
